import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, map, Observable, of, Subject, switchMap, take } from 'rxjs';
import { AnimalRepository } from "../states/animal.repository";
import { AnimalOriginRepository } from "../states/animal-origin.repository";
import { AnimalOriginModel } from "../models/animal-origin.model";
import { ContactModel } from "../../contact/models/contact.model";
import { ContactRepository } from '../../contact/states/contact.repository';
import { WaitingDialogService } from 'src/app/shared/services/waiting-dialog.service';
import { AnimalModel } from '../models/animal.model';
import { HelperService } from 'src/app/shared/services/helper.service';
import { AnimalContactRef } from '../models/animal-contact-ref.interface';
import { AnimalContactTypeEnum } from '../models/contact-type.enum';


export type AnimalAddPrefillType = 'transponder' | 'name' | 'bookNumber';
export interface AnimalAddPrefill {
  type: AnimalAddPrefillType;
  value: string;
}

export interface OriginContactEntry {
  meta: {
    id?: number;
    type: AnimalContactTypeEnum;
    label?: string | null;
    isPrimary: boolean;
  };
  data: {
    contact: ContactModel;
    isValid: boolean;
    allowEmptyValid: boolean;
  };
}


@Injectable({
  providedIn: 'root'
})
export class AnimalAddService {
  addDialogVisible = new BehaviorSubject<boolean>(false);
  private readonly _resumeAnimal = new BehaviorSubject<AnimalModel | null>(null);
  public readonly resumeAnimal$ = this._resumeAnimal.asObservable();

  public readonly originCreated$ = new Subject<{ animalId: number; origin: AnimalOriginModel }>();

  private readonly _prefill$ = new BehaviorSubject<AnimalAddPrefill | null>(null);
  prefill$ = this._prefill$.asObservable();

  constructor(
    private readonly _animalRepository: AnimalRepository,
    private readonly _animalOriginRepository: AnimalOriginRepository,
    private readonly _contactRepository: ContactRepository,
    private readonly _waittingDialogService: WaitingDialogService,
    private readonly _helperService: HelperService
  ) {
  }

  toggleAddDialog(open: boolean) {
    this.addDialogVisible.next(open);
  }

  setResumeAnimal(animal: AnimalModel | null) {
    this._resumeAnimal.next(animal);
  }

  saveOrigin(
    formGroupValues: any,
    animalId: number,
    contacts: OriginContactEntry[]
  ): void {
    const isSignable = formGroupValues.originType === 'found_animal' || formGroupValues.originType === 'secured_by_police';
    const isSigned = !!(formGroupValues.finderSignature || formGroupValues.employeeSignature || formGroupValues.handoverPartySignature);

    const originPayload: AnimalOriginModel | any = {
      animal: { id: animalId },
      contacts: [],

      dateOfReceipt: formGroupValues.dateOfReceipt ?? null,
      foundDateTime: formGroupValues.originType === 'found_animal'
        ? (formGroupValues.foundDateTime ?? null)
        : null,
      type: formGroupValues.originType,
      specialInformation: formGroupValues.originSpecialInformation,
      location: formGroupValues.originLocation,
      otherComments: formGroupValues.originOtherComments,
      foundStatus: formGroupValues.foundStatus ?? null,
      consentToShareWithOwner: !!formGroupValues.consentToShareWithOwner,
      pickupDateTime: formGroupValues.pickUpDateTime ? this._helperService.normalizeDate(formGroupValues.pickUpDateTime) : null,
      pickupUserName: formGroupValues.pickupUserName ?? null,

      careCondition: (formGroupValues.originType === 'found_animal'
        ? formGroupValues.careCondition
        : formGroupValues.originCareCondition) ?? [],
      bcsNutrition: (formGroupValues.originType === 'found_animal'
        ? formGroupValues.bcsNutrition
        : formGroupValues.originBcsNutrition) ?? null,
      temperament: (formGroupValues.originType === 'found_animal'
        ? formGroupValues.temperament
        : formGroupValues.originTemperament) ?? [],
      signatureLocation: formGroupValues.signatureLocation ?? null,
      signatureDate: isSignable && isSigned ? (formGroupValues.signatureDate ?? new Date()) : null,
      finderSignature: formGroupValues.finderSignature ?? null,
      employeeSignature: formGroupValues.employeeSignature ?? null,

      reportPdfPath: formGroupValues.reportPdfPath ?? null,
      foundSituation: formGroupValues.foundSituation ?? null,
    };

    if (formGroupValues.originType === 'secured_by_police') {
      Object.assign(originPayload, {
        dateOfReceipt: formGroupValues.dateOfReceipt ?? null,
        foundStatus: 'secured',
        location: formGroupValues.originLocation ?? null,

        handoverAuthority: formGroupValues.handoverAuthority ?? null,
        handoverResponsibleContact: formGroupValues.handoverResponsibleContact ?? null,
        ownerPresentAtHandover: !!formGroupValues.ownerPresentAtHandover,

        securingReasons: formGroupValues.securingReason ? [formGroupValues.securingReason] : [],
        securingReasonOther: formGroupValues.securingReasonOther ?? null,
        incidentCourse: formGroupValues.incidentCourse ?? null,
        operationDescription: formGroupValues.operationDescription ?? null,

        biteVictimType: formGroupValues.biteVictimType ?? null,
        biteVictimGender: formGroupValues.biteVictimGender ?? null,
        biteVictimAge: formGroupValues.biteVictimAge ?? null,

        housingConditionsDescription: formGroupValues.housingConditionsDescription ?? null,
        otherAnimalsLeftOnSite: !!formGroupValues.otherAnimalsLeftOnSite,
        otherAnimalsDescription: formGroupValues.otherAnimalsDescription ?? null,

        handedOverEquipment: formGroupValues.handedOverEquipment ?? null,
        handoverHealthStatus: formGroupValues.handoverHealthStatus ?? [],
        needsImmediateVetCare: !!formGroupValues.needsImmediateVetCare,
        healthDescription: formGroupValues.healthDescription ?? null,
        behaviourAtHandover: formGroupValues.behaviourAtHandover ?? null,

        operationStartAt: formGroupValues.operationStartAt ? this._helperService.normalizeDate(formGroupValues.operationStartAt) : null,
        operationEndAt: formGroupValues.operationEndAt ? this._helperService.normalizeDate(formGroupValues.operationEndAt) : null,

        handoverPartySignature: formGroupValues.handoverPartySignature ?? null,
        presentShelterStaff: (formGroupValues.presentShelterStaff ?? []).map((id: number) => ({ id })),

        careCondition: formGroupValues.careCondition ?? [],
        bcsNutrition: formGroupValues.bcsNutrition ?? null,
        temperament: formGroupValues.temperament ?? [],
      });
    }

    if (formGroupValues.originCompetentAuthority) {
      originPayload.competentAuthority = { id: formGroupValues.originCompetentAuthority };
    }

    const providedEntries = (contacts ?? []).filter(entry => this.isFilledContact(entry.data.contact));

    originPayload.contacts = providedEntries.map(entry => ({
      id: entry.meta.id,
      type: entry.meta.type,
      label: entry.meta.label ?? '',
      isPrimary: entry.meta.isPrimary,
      contact: null
    } as AnimalContactRef));

    providedEntries.forEach((entry, idx) => {
      if (this.isContactReferenceOnly(entry.data.contact)) {
        originPayload.contacts[idx].contact = entry.data.contact.id!;
      }
    });

    const update$ = providedEntries
      .map((entry, idx) => ({ entry, idx }))
      .filter(x => !!x.entry.data.contact?.id && this.validContact(x.entry.data.contact))
      .map(x =>
        this._contactRepository.update(x.entry.data.contact, x.entry.data.contact.id!).pipe(
          map((contact) => ({ idx: x.idx, contact }))
        )
      );

    const create$ = providedEntries
      .map((entry, idx) => ({ entry, idx }))
      .filter(x => !x.entry.data.contact?.id && this.validContact(x.entry.data.contact))
      .map(x =>
        this._contactRepository.create(this.toContactCreatePayload(x.entry.data.contact)).pipe(
          map((contact) => ({ idx: x.idx, contact }))
        )
      );

    const allRequests = [...update$, ...create$];
    if (allRequests.length === 0) {
      originPayload.contacts = (originPayload.contacts ?? []).filter((r: AnimalContactRef) => !!r.contact);
      this.createOrigin(originPayload, animalId);
      return;
    }

    forkJoin(allRequests)
      .pipe(take(1))
      .subscribe((results) => {
        results.forEach(({ idx, contact }) => {
          if (!contact?.id) return;
          originPayload.contacts[idx].contact = contact.id;
        });

        originPayload.contacts = (originPayload.contacts ?? []).filter((r: AnimalContactRef) => !!r.contact);

        this.createOrigin(originPayload, animalId);
      });
  }

  createOrigin(originPayload: AnimalOriginModel, animalId: number): void {
    this._animalOriginRepository
      .create(originPayload, animalId)
      .pipe(take(1))
      .subscribe((createdOrigin: AnimalOriginModel) => {
        this._animalRepository.addOrigin(animalId, createdOrigin);
        this._animalRepository.getById(animalId);
        this.originCreated$.next({ animalId, origin: createdOrigin });
        this._waittingDialogService.showWaitingDialog(false);
      });
  }

  resolveContactRefs(contacts: OriginContactEntry[]): Observable<AnimalContactRef[]> {
    const providedEntries = (contacts ?? []).filter(entry => this.isFilledContact(entry.data.contact));

    const refs: AnimalContactRef[] = providedEntries.map(entry => ({
      id: entry.meta.id,
      type: entry.meta.type,
      label: entry.meta.label ?? '',
      isPrimary: entry.meta.isPrimary,
      contact: null
    } as AnimalContactRef));

    providedEntries.forEach((entry, idx) => {
      if (this.isContactReferenceOnly(entry.data.contact)) {
        refs[idx].contact = entry.data.contact.id!;
      }
    });

    const update$ = providedEntries
      .map((entry, idx) => ({ entry, idx }))
      .filter(x => !!x.entry.data.contact?.id && this.validContact(x.entry.data.contact))
      .map(x =>
        this._contactRepository.update(x.entry.data.contact, x.entry.data.contact.id!).pipe(
          map((contact) => ({ idx: x.idx, contact }))
        )
      );

    const create$ = providedEntries
      .map((entry, idx) => ({ entry, idx }))
      .filter(x => !x.entry.data.contact?.id && this.validContact(x.entry.data.contact))
      .map(x =>
        this._contactRepository.create(this.toContactCreatePayload(x.entry.data.contact)).pipe(
          map((contact) => ({ idx: x.idx, contact }))
        )
      );

    const allRequests = [...update$, ...create$];
    if (allRequests.length === 0) {
      return of(refs.filter(r => !!r.contact));
    }

    return forkJoin(allRequests).pipe(
      take(1),
      map((results) => {
        results.forEach(({ idx, contact }) => {
          if (!contact?.id) return;
          refs[idx].contact = contact.id;
        });
        return refs.filter(r => !!r.contact);
      })
    );
  }

  /**
   * Legt mehrere Fundtier-Origins an, die sich denselben Finder-Kontakt teilen.
   * Der Kontakt wird EINMAL aufgelöst (kein N-faches Anlegen) und als frische
   * Ref-Kopie an jeden bereits fertig gebauten Origin-Payload gehängt.
   */
  createFoundOrigins(
    originPayloads: any[],
    contacts: OriginContactEntry[]
  ): Observable<AnimalOriginModel[]> {
    if (!originPayloads?.length) {
      return of([]);
    }

    return this.resolveContactRefs(contacts).pipe(
      take(1),
      switchMap((refs) => {
        const creates = originPayloads.map((payload) => {
          const animalId = payload?.animal?.id;
          const withContacts = {
            ...payload,
            contacts: refs.map(({ id, ...rest }) => ({ ...rest })),
          };
          return this._animalOriginRepository.create(withContacts, animalId).pipe(take(1));
        });
        return forkJoin(creates);
      })
    );
  }

  isFilledContact(contact: ContactModel): boolean {
    return !!contact && !!(contact.id || this.validContact(contact));
  }

  private isContactReferenceOnly(contact: ContactModel): boolean {
    return !!contact?.id && !this.validContact(contact);
  }

  validContact(contact: ContactModel): boolean {
    return !!(contact?.firstName || contact?.lastName || contact?.companyName);
  }

  setPrefill(prefill: AnimalAddPrefill | null) {
    this._prefill$.next(prefill);
  }

  private toContactCreatePayload(contact: ContactModel): Partial<ContactModel> {
    const {
      id,
      createdAt,
      updatedAt,
      createdDate,
      updatedDate,
      ...rest
    } = (contact ?? {}) as any;

    return rest;
  }
}
